import {
  ContentContainer,
  IconContainer,
  ToastContent,
  ToastMessage,
  ToastTitle,
  ToastWrapper,
} from "@/components/Toast/style";
import { TEST_IDS } from "@/constants/testIds";
import type { Toast } from "@/contexts/toastContext";
import { useRouter } from "@/hooks/useRouter";
import { useToast } from "@/hooks/useToast";
import {
  SvgCheckCircleFilled,
  SvgCrossCircleFilled,
  SvgSms,
} from "@getgrover/ui";
import { useEffect, useRef, useState } from "react";

interface ToastItemProps {
  toast: Toast;
}

export const ToastItem: React.FC<ToastItemProps> = ({ toast }) => {
  const { removeToast } = useToast();
  const router = useRouter();
  const contentRef = useRef<HTMLButtonElement>(null);
  const [height, setHeight] = useState<number | undefined>(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const getIconByType = (type: Toast["type"]) => {
    switch (type) {
      case "success":
        return <SvgCheckCircleFilled />;
      case "error":
        return <SvgCrossCircleFilled />;
      case "message":
        return <SvgSms />;
      default:
        return "";
    }
  };

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      removeToast(toast.id);
    }, 600);
  };

  const handleToastClick = () => {
    toast.onClick?.();

    if (toast.dismissible) {
      handleClose();
    }

    if (toast.link) {
      router.push(toast.link);
    }
  };

  useEffect(() => {
    if (contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;
      setHeight(contentHeight + 8);

      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, toast.duration);

      return () => clearTimeout(timer);
    }
  }, [toast.duration]);

  useEffect(() => {
    if (isExiting) {
      setTimeout(() => {
        setHeight(0);
      }, 300);
    }
  }, [isExiting]);

  return (
    <ToastWrapper $height={height} $isVisible={isVisible}>
      <ToastContent
        ref={contentRef}
        role="alert"
        data-testid={`${TEST_IDS.TOAST_ITEM}-${toast.type}`}
        $isLink={!!toast.link}
        $isExiting={isExiting}
        $isVisible={isVisible}
        onClick={handleToastClick}
      >
        <IconContainer $type={toast.type}>
          {getIconByType(toast.type)}
        </IconContainer>

        <ContentContainer>
          {toast.title && <ToastTitle>{toast.title}</ToastTitle>}
          <ToastMessage>{toast.message}</ToastMessage>
        </ContentContainer>
      </ToastContent>
    </ToastWrapper>
  );
};
